import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Icon, Popup } from 'semantic-ui-react';
import { Table } from 'components';
import styled from 'styled-components';
import { translateAssetClass, translateText } from '../../../../utils/helpers';
import assignColors, { getChartColors } from '../../../../utils/assignColors';

@connect((state) => ({
  french: state.Storage.language === 'fr',
  partnerName: state.Storage['partner-name'],
}))
class ExposureTable extends Component {
  getColor(index, value) {
    const { assetClass, individualAsset, partnerName } = this.props;
    const colors = getChartColors(partnerName);
    if (assetClass) {
      return assignColors([value], partnerName)[0];
    }
    if (individualAsset && index >= 10) {
      return colors[10];
    }
    return colors[index];
  }

  isInt = (num) => (num * 100) % 1 === 0;

  isLong = (data) => data.length > 11;

  renderText = (value) => {
    const { french, individualAsset, assetClass, pdf } = this.props;
    const displayText = translateText(value, french);

    if (value === 'Port. Specific Risk') {
      return (
        <div>
          {french ? displayText : 'Portfolio Specific Risk'}
          {!pdf && (
            <Popup
              trigger={
                <Icon
                  name="question circle outline"
                  style={{ fontSize: '15px', verticalAlign: 'initial' }}
                />
              }
              position="right center"
              wide
            >
              {french
                ? "Le risque spécifique au portefeuille est la part de la volatilité de votre portefeuille qui ne peut pas être expliquée par les mouvements des marchés boursiers régionaux, des secteurs boursiers mondiaux, des variables macroéconomiques ou des facteurs de style d'équité (selon le type d'analyse en question). Par exemple, votre exposition au risque aux marchés boursiers régionaux peut être minimale si vous détenez principalement des produits à revenu fixe."
                : "Portfolio-specific risk is the portion of your portfolio's volatility that cannot be explained by movements in regional stock markets, global stock sectors, macroeconomic variables, or equity style factors (depending on the type of analysis in question). For example, your risk exposure to regional stock markets may be minimal if you hold predominantly fixed income products."}
            </Popup>
          )}
        </div>
      );
    }
    if (value === 'Other') {
      return (
        <div>
          {assetClass
            ? translateAssetClass(french, 'Asset Allocation Funds')
            : displayText}
          {!pdf && (
            <Popup
              trigger={
                <Icon
                  name="question circle outline"
                  style={{ fontSize: '15px', verticalAlign: 'initial' }}
                />
              }
              position="right center"
              wide
            >
              {french
                ? 'Voir les informations importantes sur les fonds équilibrés/fonds d’allocation d’actifs ci-dessus.'
                : 'See important information about balanced funds/asset allocation funds above.'}
            </Popup>
          )}
        </div>
      );
    }
    if (value === 'Cash') {
      return (
        <div>
          {displayText}
          {!pdf && (
            <Popup
              trigger={
                <Icon
                  name="question circle outline"
                  style={{ fontSize: '15px', verticalAlign: 'initial' }}
                />
              }
              position="right center"
            >
              {french
                ? "Les espèces sont des liquidités non rémunérées dans votre compte si vous avez choisi d'analyser avec des liquidités."
                : 'Cash is non-interest bearing cash in your account if you chose to analyze with cash.'}
            </Popup>
          )}
        </div>
      );
    }
    if (value === 'Asset Allocation') {
      return (
        <div>
          {value}
          {!pdf && (
            <Popup
              trigger={
                <Icon
                  name="question circle outline"
                  style={{
                    fontSize: '15px',
                    verticalAlign: 'initial',
                    color: 'dimgrey',
                  }}
                />
              }
              position="right center"
            >
              {french
                ? 'Comprend les fonds de fonds et les fonds équilibrés.'
                : 'Includes fund of funds and balanced funds.'}
            </Popup>
          )}
        </div>
      );
    }
    if (individualAsset) {
      return (
        <HoldingsCell>
          <div className="ticker">{value.ticker}</div>
          <div>{value.name}</div>
        </HoldingsCell>
      );
    }
    if (value === 'Consumer Disc.') {
      return (
        <div>
          {french ? translateText(value, french) : 'Consumer Discretionary'}
        </div>
      );
    }

    return <div>{displayText}</div>;
  };

  render() {
    const {
      align,
      headers,
      isExcluded,
      totals,
      data,
      french,
      individualAsset,
      pdf,
      fullWidth,
    } = this.props;
    const translatedHeaders = headers.map((header) => {
      switch (header) {
        case 'Region':
          return french ? 'Région' : 'Region';
        case 'Holdings Breakdown':
          return french ? 'Répartition des avoirs' : 'Holdings Breakdown';
        case 'Risk Exposure':
          return french ? 'Exposition au risque' : 'Risk Exposure';
        default:
          return header;
      }
    });
    const filteredData = data
      .map((row, index) => ({ row, index }))
      .filter(({ row }) =>
        Object.values(row).some((value) => {
          const num = Number(value);
          return !Number.isNaN(num) && num !== 0;
        })
      );

    return (
      <Container
        height={!pdf ? (this.isLong(data) ? '500px' : null) : null}
        pdf={pdf}
        fullWidth={fullWidth}
        individual={individualAsset}
      >
        <Table basic textAlign={align || 'center'}>
          <Table.Header>
            <Table.Row>
              {translatedHeaders.map((header, idx) => (
                <Table.HeaderCell key={idx}>
                  <div className={idx === 0 ? 'first' : ''}>{header}</div>
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              /* eslint-disable */
              filteredData.map(({ row, index }) => (
                <Table.Row key={index} negative={isExcluded}>
                  {Object.values(row).map((value, idx2) =>
                    Number(value) ? (
                      <Table.Cell key={idx2}>
                        <div className={idx2 === 0 ? 'first' : ''}>
                          {idx2 === 0 && (
                            <Dot color={this.getColor(index, value)} />
                          )}
                          {this.props.portfolioLevel !== undefined &&
                          this.props.portfolioLevel
                            ? `${Math.round(value * 10000) / 10000}`
                            : this.isInt(value)
                            ? `${Math.round(value * 1000) / 10}%`
                            : `${Math.round(value * 10000) / 100}%`}
                        </div>
                      </Table.Cell>
                    ) : (
                      <Table.Cell key={idx2} style={{ maxWidth: '21rem' }}>
                        <div className={idx2 === 0 ? 'first' : ''}>
                          {idx2 === 0 && (
                            <Dot color={this.getColor(index, value)} />
                          )}
                          {!value ? '-' : this.renderText(value)}
                        </div>{' '}
                      </Table.Cell>
                    )
                  )}
                </Table.Row>
              ))
              /* eslint-enable */
            }
            {totals !== undefined ? (
              <Table.Row>
                {
                  /* eslint-disable */
                  totals.map((total, idx) => (
                    <Table.Cell key={idx}>
                      <div className={idx === 0 ? 'first' : ''}>
                        <b>{total}</b>
                      </div>
                    </Table.Cell>
                  ))
                }
              </Table.Row>
            ) : (
              <br />
            )}
          </Table.Body>
        </Table>
      </Container>
    );
  }
}

ExposureTable.propTypes = {
  headers: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  align: PropTypes.string,
  isExcluded: PropTypes.bool,
  totals: PropTypes.array,
  portfolioLevel: PropTypes.bool,
  french: PropTypes.bool,
  individualAsset: PropTypes.bool,
  assetClass: PropTypes.bool,
  pdf: PropTypes.bool,
  fullWidth: PropTypes.bool,
  partnerName: PropTypes.string,
};

ExposureTable.defaultProps = {
  align: '',
  isExcluded: false,
  totals: [],
  portfolioLevel: false,
  french: false,
  individualAsset: false,
  assetClass: false,
  pdf: false,
  fullWidth: false,
  partnerName: '',
};

export default ExposureTable;

const Container = styled.div`
  overflow: ${(props) => (props.pdf ? 'visible' : 'auto')};
  height: ${(props) => props.height};
  margin-top: 3rem;
  padding: ${(props) => props.fullWidth && '0 5% 0 5%'};

  .first {
    display: flex;
    flex-direction: row;
    text-align: left;
    column-gap: 10px;
    align-items: center;
    padding-left: ${(props) =>
      props.fullWidth && (props.individual ? '5%' : '10%')};
  }
`;

const HoldingsCell = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;

  .ticker {
    font-weight: bold;
  }
`;

const Dot = styled.div`
  height: 12px;
  width: 12px;
  min-width: 12px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
`;
